import { File, FileAudio, FileSpreadsheet, FileText, FileVideo, Type, type LucideIcon } from 'lucide-react'
import type { SVGProps } from 'react'

/**
 * PDF: lucide's file outline with a red "PDF" plate (Acrobat-style, not the
 * trademarked Adobe glyph). Same props shape as a lucide icon so call sites
 * don't care which they got.
 */
export function PdfIcon({ strokeWidth = 1.5, ...props }: SVGProps<SVGSVGElement> & { strokeWidth?: number | string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <rect x="2.5" y="13" width="15" height="7" rx="1.2" fill="#dc2626" stroke="none" />
      <text x="10" y="18.4" textAnchor="middle" fontSize="5" fontWeight="700" fontFamily="system-ui, sans-serif" fill="#fff" stroke="none">PDF</text>
    </svg>
  )
}


/**
 * AB#662: file-type icon for non-image media (pdf, word, excel, markdown, txt,
 * csv, video, audio). Images use a real thumbnail, never this.
 *
 * ponytail: mime-first with a filename fallback, because a .md often arrives as
 * text/plain and legacy Office mimes are inconsistent across browsers.
 */
export function mediaIcon(mimeType?: string, filename?: string): LucideIcon | typeof PdfIcon {
  const mime = mimeType ?? ''
  const ext = filename?.split('.').pop()?.toLowerCase() ?? ''

  if (mime === 'application/pdf' || ext === 'pdf') return PdfIcon
  if (mime.startsWith('video/')) return FileVideo
  if (mime.startsWith('audio/')) return FileAudio
  if (mime.startsWith('font/') || ['woff', 'woff2', 'ttf', 'otf'].includes(ext)) return Type
  if (mime.includes('spreadsheet') || mime === 'application/vnd.ms-excel' || ext === 'csv' || ext === 'xls' || ext === 'xlsx') {
    return FileSpreadsheet
  }
  if (mime === 'application/pdf' || mime.startsWith('text/') || mime.includes('word') || ['pdf', 'doc', 'docx', 'md', 'txt'].includes(ext)) {
    return FileText
  }
  return File
}

/** Short uppercase type label for a media item, e.g. "PDF", "DOCX", "MD". */
export function mediaTypeLabel(mimeType?: string, filename?: string): string {
  const ext = filename?.split('.').pop()?.toLowerCase()
  // <= 5 so 'woff2' keeps its own label instead of falling back to the mime
  if (ext && ext.length <= 5) return ext.toUpperCase()
  return (mimeType?.split('/')[1] ?? 'file').toUpperCase()
}
